/**
 * 着荷取消(巻き戻し)モーダル
 *
 * 設計書 05-frontend-design.md §5.5 整合。
 * 「帳簿訂正」ニュアンスの警告、期限カウントダウン、Popconfirm 二重確認、
 * REVERSAL_NOT_ALLOWED エラー時の代替手順提示を含む。
 */
import { useState } from 'react';
import { Button, Modal, Form, Input, Descriptions, Alert, Tag, Popconfirm, message, Space } from 'antd';
import { logger } from '@/shared/lib/logger';
import { reverseArrival } from '../services/purchaseOrderApiService';
import { formatDateTimeShort } from '@/shared/utils/dateFormatter';
import { DateFactory } from '@/shared/utils/dateFactory';
import { PURCHASE_ORDER_BUSINESS_RULES } from '../types/purchaseOrder';
import type {
  ReverseArrivalRequest,
  ReverseArrivalResult,
  PurchaseOrderViewModel,
} from '../types/purchaseOrder';

const { TextArea } = Input;

const REVERSAL_REASON_TEXTAREA_ROWS = 3;
const HOURS_PER_DAY = 24;
const SECONDS_PER_HOUR = 3600;
const MS_PER_SECOND = 1000;
const MS_PER_DAY = HOURS_PER_DAY * SECONDS_PER_HOUR * MS_PER_SECOND;
/**
 * REVERSAL_NOT_ALLOWED エラー時の代替手順文言。
 * Axios エラー or Error.message に含まれる識別子で判定。
 */
const REVERSAL_NOT_ALLOWED_MARKER = 'REVERSAL_NOT_ALLOWED';
const REVERSAL_NOT_ALLOWED_GUIDANCE =
  '入庫確認後に在庫変動があるため、巻き戻しはできません。修正は「消費」登録で対応してください。';

interface FormValues {
  readonly reversalReason?: string;
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onReversed: (params: { readonly result: ReverseArrivalResult }) => void;
  readonly purchaseOrder: PurchaseOrderViewModel;
}

interface ReversalDeadlineInfo {
  readonly deadlineDate: Date;
  readonly remainingDays: number;
}

/**
 * 巻き戻し期限と残日数を算出
 *
 * arrivedAt + REVERSAL_DEADLINE_DAYS が期限。
 * `arrivedAt === null` の場合は呼出側で表示自体スキップする想定。
 */
function calculateReversalDeadlineInfo(params: {
  readonly arrivedAt: Date;
}): ReversalDeadlineInfo {
  const deadlineMs =
    params.arrivedAt.getTime() +
    PURCHASE_ORDER_BUSINESS_RULES.REVERSAL_DEADLINE_DAYS * MS_PER_DAY;
  const deadlineDate = DateFactory.fromTimestamp(deadlineMs);
  const remainingMs = deadlineMs - DateFactory.now().getTime();
  const remainingDays = Math.max(0, Math.ceil(remainingMs / MS_PER_DAY));
  return { deadlineDate, remainingDays };
}

/**
 * Service エラーから REVERSAL_NOT_ALLOWED 識別を行う
 */
function isReversalNotAllowedError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.message.includes(REVERSAL_NOT_ALLOWED_MARKER);
}

export function ArrivalReversalModal({
  open,
  onClose,
  onReversed,
  purchaseOrder,
}: Props): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const deadlineInfo =
    purchaseOrder.arrivedAt !== null
      ? calculateReversalDeadlineInfo({ arrivedAt: purchaseOrder.arrivedAt })
      : null;

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const request: ReverseArrivalRequest = {
        reversalReason: values.reversalReason ?? null,
      };
      const result = await reverseArrival({
        purchaseOrderId: purchaseOrder.id,
        request,
      });
      message.success('入庫を取り消しました(発注中状態に戻しました)');
      form.resetFields();
      onReversed({ result });
    } catch (error) {
      if (isReversalNotAllowedError(error)) {
        message.error(REVERSAL_NOT_ALLOWED_GUIDANCE);
      } else {
        const errorMessage =
          error instanceof Error ? error.message : '入庫取消に失敗しました';
        message.error(errorMessage);
      }
      logger.captureError(error, `ArrivalReversalModal.handleSubmit(${purchaseOrder.id})`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="入庫取消(帳簿訂正)"
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          キャンセル
        </Button>,
        <Popconfirm
          key="confirm"
          title="本当に入庫取消しますか?"
          description="1発注につき1回のみ実行可能です。"
          okText="取消する"
          cancelText="やめる"
          onConfirm={() => form.submit()}
        >
          <Button type="primary" danger loading={isSubmitting}>
            取消する
          </Button>
        </Popconfirm>,
      ]}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        message="帳簿上の入庫確認を取り消し、再度「入庫待ち」状態に戻します。"
        description="物理的な材料の扱いは変わりません(誤入庫登録の訂正用です)。在庫数量は発注数量分減算されます。"
        style={{ marginBottom: 16 }}
      />

      {deadlineInfo !== null && (
        <Space size="small" style={{ marginBottom: 16 }}>
          <Tag color="orange">
            巻き戻し可能期限: {formatDateTimeShort(deadlineInfo.deadlineDate)}まで(残
            {deadlineInfo.remainingDays}日)
          </Tag>
          <Tag>
            巻き戻し済回数: {purchaseOrder.reversalCount}/
            {PURCHASE_ORDER_BUSINESS_RULES.REVERSAL_MAX_COUNT}
          </Tag>
        </Space>
      )}

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="現場">{purchaseOrder.siteName}</Descriptions.Item>
        <Descriptions.Item label="材料">{purchaseOrder.materialTypeName}</Descriptions.Item>
        <Descriptions.Item label="発注数量">{purchaseOrder.orderedQuantity}</Descriptions.Item>
        {purchaseOrder.lastArrivedAt !== null && (
          <Descriptions.Item label="最終入庫確認日時">
            {formatDateTimeShort(purchaseOrder.lastArrivedAt)}
          </Descriptions.Item>
        )}
        {purchaseOrder.lastArrivedByUserName !== null && (
          <Descriptions.Item label="最終入庫確認者">
            {purchaseOrder.lastArrivedByUserName}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="reversalReason" label="取消理由(任意)">
          <TextArea rows={REVERSAL_REASON_TEXTAREA_ROWS} placeholder="取消理由を入力" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
